// One live therapist SOS — 0273, 0277. The SAME card on every surface: the page
// banner, the live map's pinned lane. Three copies is how they start to
// disagree.
//
// Layered, after Leon's reference (2026-10-01): a white card resting on a
// tinted base whose footer strip carries the time. The base says the stage:
//
//   open          red — nobody has it. Primary action: Acknowledge (confirm).
//   acknowledged  amber — someone has it, named. Primary action: Mark resolved
//                 (required note). It stays here until then.
//
// ⚠ NEVER AUTO-DISMISSES. Only a resolution takes it off screen.
//
// COMPACT (the live map's column, Leon 2026-10-23: three full cards "eat the
// whole screen"): one row — who, how serious, who has it and since when — and
// the one action. The SOS ID, the cancelled booking and the location line stay
// on the full card (page banner) and in both dialogs.

import { type ReactNode, useState } from "react";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { formatTimeAgo } from "@bednbody/ui/lib/format";
import { cn } from "@bednbody/ui/lib/utils";
import {
  CalendarXIcon,
  CheckCircleIcon,
  CrosshairIcon,
  UserCheckIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { CopyableCode } from "@/components/CopyableCode";
import { AcknowledgeSosDialog, ResolveSosDialog } from "./SosDialogs";
import type { SosAlertView } from "./sos.model";

const STAGE = {
  open: {
    // The tint is a gradient laid OVER a solid card colour: the lane floats on
    // the map, and a translucent base would let the streets through.
    base: "from-destructive/10 to-destructive/20 ring-destructive/20",
    mark: "bg-destructive text-destructive-foreground",
    label: "text-destructive",
    labelText: "Therapist SOS",
  },
  acknowledged: {
    base: "from-warning/10 to-warning/20 ring-warning/25",
    // Soft, not solid: being handled is calmer than open, and the palette has
    // no on-warning foreground token to put white-on-amber on.
    mark: "bg-warning/15 text-warning",
    label: "text-warning",
    labelText: "SOS · being handled",
  },
} as const;

interface SosAlertCardProps {
  alert: SosAlertView;
  /** A second button beside the main one — "Show on map", "View on live map". */
  action?: ReactNode;
  className?: string;
  /** One row instead of the full card — the live map's column. */
  compact?: boolean;
}

export function SosAlertCard({ alert, action, className, compact = false }: SosAlertCardProps) {
  const [dialog, setDialog] = useState<"acknowledge" | "resolve" | null>(null);
  const stage = STAGE[alert.stage];
  const isOpen = alert.stage === "open";

  const dialogs = (
    <>
      <AcknowledgeSosDialog
        alert={alert}
        onOpenChange={(open) => setDialog(open ? "acknowledge" : null)}
        open={dialog === "acknowledge"}
      />
      <ResolveSosDialog
        alert={alert}
        onOpenChange={(open) => setDialog(open ? "resolve" : null)}
        open={dialog === "resolve"}
      />
    </>
  );

  if (compact) {
    return (
      <article
        aria-label={`SOS from ${alert.therapistName}`}
        className={cn(
          "flex items-center gap-2.5 rounded-xl bg-card p-2.5 shadow-card ring-1",
          isOpen ? "ring-2 ring-destructive" : "ring-warning/40",
          className,
        )}
        role={isOpen ? "alert" : undefined}
      >
        <div className={cn("grid size-8 shrink-0 place-items-center rounded-full", stage.mark)}>
          {isOpen ? (
            <WarningIcon className="size-4" weight="fill" />
          ) : (
            <UserCheckIcon className="size-4" weight="fill" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-foreground">
            {alert.therapistName}
            {alert.dangerLabel ? (
              <span className="font-normal text-muted-foreground"> · {alert.dangerLabel}</span>
            ) : null}
          </h3>
          <p className={cn("truncate text-xs font-medium", stage.label)}>
            {isOpen
              ? `Nobody has it · ${formatTimeAgo(alert.raisedAt)}`
              : `${alert.handlerName ?? "Someone"} has it · ${formatTimeAgo(
                  alert.acknowledgedAt ?? alert.raisedAt,
                )}`}
          </p>
        </div>
        {action}
        {isOpen ? (
          <Button onClick={() => setDialog("acknowledge")} size="sm" variant="sos">
            I'll take it
          </Button>
        ) : (
          <Button onClick={() => setDialog("resolve")} size="sm" variant="outline">
            Resolve
          </Button>
        )}
        {dialogs}
      </article>
    );
  }

  return (
    <article
      aria-label={`SOS from ${alert.therapistName}`}
      className={cn(
        "rounded-card bg-card bg-linear-to-b p-1 shadow-float ring-1",
        stage.base,
        className,
      )}
      role={isOpen ? "alert" : undefined}
    >
      <SurfaceCard className="space-y-2 rounded-2xl p-3">
        <div className="flex items-center gap-2">
          <div className={cn("grid size-8 shrink-0 place-items-center rounded-full", stage.mark)}>
            {isOpen ? (
              <WarningIcon className="size-4" weight="fill" />
            ) : (
              <UserCheckIcon className="size-4" weight="fill" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-foreground">
              {alert.therapistName}
              {alert.hubName ? (
                <span className="font-normal text-muted-foreground"> · {alert.hubName}</span>
              ) : null}
            </h3>
            <p className={cn("text-xs font-medium", stage.label)}>{stage.labelText}</p>
          </div>
          {alert.dangerLabel ? <Badge variant="destructive-soft">{alert.dangerLabel}</Badge> : null}
        </div>

        <CopyableCode label="SOS ID" value={alert.ref} />

        <ul
          aria-label="What happened"
          className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground"
        >
          <li className="inline-flex items-center gap-1">
            <CalendarXIcon aria-hidden="true" className="size-3.5" />
            {alert.bookingRefs.length > 0
              ? `${alert.bookingRefs.join(", ")} cancelled`
              : "No booking affected"}
          </li>
          <li className="inline-flex items-center gap-1">
            <CrosshairIcon aria-hidden="true" className="size-3.5" />
            {alert.locationLine}
          </li>
        </ul>

        <div className="flex gap-2">
          {action}
          {isOpen ? (
            <Button
              className="flex-1"
              onClick={() => setDialog("acknowledge")}
              size="sm"
              variant="sos"
            >
              Acknowledge
            </Button>
          ) : (
            <Button className="flex-1" onClick={() => setDialog("resolve")} size="sm">
              <CheckCircleIcon data-icon="inline-start" weight="fill" />
              Mark resolved
            </Button>
          )}
        </div>
      </SurfaceCard>

      <p className={cn("px-3 py-1 text-center text-xs font-medium", stage.label)}>
        Raised {formatTimeAgo(alert.raisedAt)}
        {isOpen
          ? " · nobody has it yet"
          : ` · handled by ${alert.handlerName}${
              alert.acknowledgedAt ? `, ${formatTimeAgo(alert.acknowledgedAt)}` : ""
            }`}
      </p>

      {dialogs}
    </article>
  );
}
